import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PartnershipsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const partners = [
    {
      category: "Research Institutions",
      partners: [
        {
          name: "MIT Quantum Engineering Center",
          logo: "MIT",
          description: "Joint research on quantum field applications in distributed computing",
          since: "2022"
        },
        {
          name: "University of Nairobi",
          logo: "UN",
          description: "African mathematical foundations research collaboration",
          since: "2021"
        },
        {
          name: "CERN",
          logo: "CERN",
          description: "High-energy physics simulations using EADC",
          since: "2023"
        }
      ]
    },
    {
      category: "Technology Partners",
      partners: [
        {
          name: "NVIDIA",
          logo: "NV",
          description: "Hardware acceleration for electromagnetic field computation",
          since: "2023"
        },
        {
          name: "IBM Quantum",
          logo: "IBM",
          description: "Quantum-classical hybrid computing integration",
          since: "2023"
        }
      ]
    },
    {
      category: "Strategic Investors",
      partners: [
        {
          name: "Breakthrough Energy Ventures",
          logo: "BEV",
          description: "Supporting sustainable computing infrastructure",
          since: "2022"
        },
        {
          name: "African Development Bank",
          logo: "ADB",
          description: "Funding African technology leapfrogging initiatives",
          since: "2021"
        }
      ]
    }
  ];

  return (
    <>
      <style>{`
        .partnerships-hero {
          padding: 10rem 2rem 4rem;
          background: linear-gradient(135deg, var(--blue-50), var(--purple-50));
          text-align: center;
        }

        .partnerships-hero-content {
          max-width: 1280px;
          margin: 0 auto;
        }

        .partnerships-title {
          font-size: clamp(3rem, 6vw, 4.5rem);
          font-weight: 800;
          color: var(--black);
          margin-bottom: 1.5rem;
          letter-spacing: -0.05em;
        }

        .partnerships-description {
          font-size: 1.375rem;
          color: var(--gray-600);
          line-height: 1.6;
          max-width: 800px;
          margin: 0 auto;
        }

        .partners-section {
          padding: 6rem 2rem;
        }

        .partners-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .partner-category {
          margin-bottom: 4rem;
        }

        .category-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 2rem;
        }

        .partners-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
        }

        .partner-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 2rem;
          transition: all var(--transition-base);
          display: flex;
          gap: 1.5rem;
          align-items: flex-start;
        }

        .partner-card:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-2px);
          border-color: var(--blue-200);
        }

        .partner-logo {
          width: 80px;
          height: 80px;
          background: var(--gradient-primary);
          border-radius: var(--radius);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--white);
          flex-shrink: 0;
        }

        .partner-info {
          flex: 1;
        }

        .partner-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .partner-description {
          font-size: 1rem;
          color: var(--gray-600);
          line-height: 1.6;
          margin-bottom: 0.5rem;
        }

        .partner-since {
          font-size: 0.875rem;
          color: var(--gray-500);
        }

        .cta-section {
          padding: 6rem 2rem;
          background: var(--gray-900);
          color: var(--white);
          text-align: center;
        }

        .cta-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1rem;
        }

        .cta-description {
          font-size: 1.25rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }

        .cta-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          background: var(--blue-600);
          color: var(--white);
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 700;
          font-size: 1.125rem;
          transition: all var(--transition-base);
        }

        .cta-button:hover {
          background: var(--blue-500);
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
        }

        @media (max-width: 768px) {
          .partnerships-hero {
            padding: 8rem 1rem 3rem;
          }

          .partners-grid {
            grid-template-columns: 1fr;
          }

          .partner-card {
            flex-direction: column;
            text-align: center;
          }

          .partner-logo {
            margin: 0 auto;
          }
        }
      `}</style>

      <section className="partnerships-hero">
        <div className="partnerships-hero-content">
          <h1 className="partnerships-title">
            Building the Future Together
          </h1>
          <p className="partnerships-description">
            Our partnerships span continents and disciplines, uniting the world's brightest minds and most innovative organizations in pursuit of computational transcendence.
          </p>
        </div>
      </section>

      <section className="partners-section">
        <div className="partners-container">
          {partners.map((category, index) => (
            <div key={index} className="partner-category">
              <h2 className="category-title">{category.category}</h2>
              <div className="partners-grid">
                {category.partners.map((partner, idx) => (
                  <div key={idx} className="partner-card">
                    <div className="partner-logo">{partner.logo}</div>
                    <div className="partner-info">
                      <h3 className="partner-name">{partner.name}</h3>
                      <p className="partner-description">{partner.description}</p>
                      <p className="partner-since">Partner since {partner.since}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="cta-section">
        <h2 className="cta-title">Become a Partner</h2>
        <p className="cta-description">
          Join us in revolutionizing computation and solving humanity's greatest challenges
        </p>
        <Link to="/contact" className="cta-button">
          Start Partnership Discussion
          <span>→</span>
        </Link>
      </section>
    </>
  );
};

export default PartnershipsPage;
